import Joi, { ValidationResult } from 'joi';

import { ValidationFunctionResult } from '../../../../shared/types/validation-function-result';
import { setOperationStatusSchema } from '../../../../shared/validators/schemas/set-operation-status.schema';
import { checkInOutActionValidationSchema } from '../../../shared/validators/schemas/check-in-out-validation.schema';
import { RentalAgreementRecordDto } from '../../dtos/rental-agreement-record.dto';
import { RentalAgreementActionEnum } from '../../enums/rental-agreement-action.enum';
import { RentalAgreementStatusEnum } from '../../enums/rental-agreement-status.enum';

/**
 * Validates if an action can be performed by validating the record data.
 * @param record - The record for the data validation.
 * @param action - The action to be validated.
 * @param abortEarly - Whether to abort the validation on the first error.
 * @returns The validation result.
 */
export const canPerformAction = (
	record: RentalAgreementRecordDto,
	action: RentalAgreementActionEnum,
	abortEarly: boolean = false,
): ValidationFunctionResult => {
	// Check if record is provided
	if (!record) throw new Error('record is required.');

	let validationResult: ValidationResult | undefined;

	switch (action) {
		case RentalAgreementActionEnum.COMPLETE:
			validationResult = setOperationStatusSchema(
				RentalAgreementStatusEnum.COMPLETED,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalAgreementActionEnum.APPROVE:
			// TODO: Approval flow
			break;

		case RentalAgreementActionEnum.CANCEL:
			validationResult = setOperationStatusSchema(
				RentalAgreementStatusEnum.CANCELLED,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalAgreementActionEnum.PENDING:
			validationResult = setOperationStatusSchema(
				RentalAgreementStatusEnum.PENDING,
			).validate(record, {
				abortEarly,
			});
			break;

		case RentalAgreementActionEnum.IN_PROGRESS:
			break;

		case RentalAgreementActionEnum.CHECK_IN:
		case RentalAgreementActionEnum.CHECK_OUT:
			validationResult = checkInOutActionValidationSchema().validate(record, {
				abortEarly,
			});
			break;

		case RentalAgreementActionEnum.REJECT:
			// TODO: Approval flow
			break;

		case RentalAgreementActionEnum.CREATE_MAINTENANCE:
			break;

		case RentalAgreementActionEnum.CREATE_DAMAGE:
			break;

		case RentalAgreementActionEnum.SEND_PAYMENT_LINK:
			validationResult = Joi.object({
				status: Joi.string()
					.valid(
						RentalAgreementStatusEnum.PENDING,
						RentalAgreementStatusEnum.IN_PROGRESS,
						RentalAgreementStatusEnum.COMPLETED,
					)
					.required(),
			})
				.unknown(true)
				.validate(record, { abortEarly });
			break;

		default:
			throw new Error(`Unknown action: ${action}`);
	}

	// Check for validation errors
	if (validationResult?.error) {
		return {
			isValid: false,
			errors: validationResult.error.details?.map((e) => e.message),
		};
	}

	// Return valid result if no errors
	return { isValid: true };
};
